import { useCallback, useMemo, useState } from "react";
import type { FileItem } from "../../types";
import { Button } from "@/shared/ui/button";
import { useFileDownload } from "../../hooks/useFileDownload";
import { useFileDelete } from "../../hooks/useFileDelete";
import { getFileActions } from "../../utils/fileUtils";
import { DeleteFileDialog } from "./DeleteFileDialog";
import { EditTagsDialog } from "./EditTagsDialog";
import { EditFileAttributesDialog } from "./EditFileAttributesDialog";
import { FilePreviewSection } from "./FilePreviewSection";
import { FileNavigationHeader } from "./FileNavigationHeader";
import { FileMetadataSection } from "./FileMetadataSection";
import { FileActionsGrid } from "./FileActionsGrid";
import { CommentsSection } from "./CommentsSection";
import {
  createComment,
  updateComment,
  deleteComment,
} from "@/services/api/comments";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/shared/lib/toast";
import { useCategory } from "@/features/admin/queries/useCategories";
import { useCategoryAttributesDisplay } from "@/features/admin/panels/categories/hooks/useCategoryAttributesDisplay";
import type { DisplayAttribute } from "@/features/admin/panels/categories/hooks/useCategoryAttributesDisplay";
import { FileCog, X } from "lucide-react";

interface FileDetailsPaneProps {
  file: FileItem;
  files: FileItem[];
  onClose: () => void;
  onNavigate: (direction: "prev" | "next") => void;
  onImageClick?: (file: FileItem) => void;
  canDelete?: boolean;
  categoryId?: string | number;
  attributeOrder?: string[];
  categoryAttributes?: DisplayAttribute[];
}

export function FileDetailsPane({
  file,
  files,
  onClose,
  onNavigate,
  onImageClick,
  canDelete = true,
  categoryId,
  attributeOrder,
  categoryAttributes,
}: FileDetailsPaneProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();

  const comments = file.comments || [];

  const [showEditAttributes, setShowEditAttributes] = useState(false);
  const [showEditTags, setShowEditTags] = useState(false);
  const { isDownloading, downloadFile } = useFileDownload({ file });
  const { isDeleting, deleteFile: deleteFileHandler } = useFileDelete({
    file,
    categoryId,
    onDeleteSuccess: () => {
      onClose();
    },
  });

  const { data: category } = useCategory(categoryId || "", {
    enabled: !!categoryId && showEditAttributes,
  });

  const {
    attributes: categoryAttributesForEdit,
    isLoading: isLoadingAttributes,
  } = useCategoryAttributesDisplay({
    category: category || null,
    enabled: showEditAttributes && !!category,
  });

  const { canNavigatePrev, canNavigateNext } = useMemo(() => {
    const index = files.findIndex((f) => f.id === file.id);
    return {
      canNavigatePrev: index > 0,
      canNavigateNext: index < files.length - 1,
    };
  }, [files, file.id]);

  const FILE_ACTIONS = useMemo(
    () =>
      getFileActions(
        downloadFile,
        isDownloading,
        deleteFileHandler,
        isDeleting
      ),
    [downloadFile, isDownloading, deleteFileHandler, isDeleting]
  );

  const visibleActions = useMemo(() => {
    let actions = canDelete
      ? FILE_ACTIONS
      : FILE_ACTIONS.filter((action) => action.label !== "Delete");

    if (categoryId) {
      const editTagsIndex = actions.findIndex(
        (action) => action.label === "Edit tags"
      );
      if (editTagsIndex !== -1) {
        const editAttributesAction = {
          icon: FileCog,
          label: "Edit attributes",
          onClick: () => setShowEditAttributes(true),
        };
        actions = [
          ...actions.slice(0, editTagsIndex + 1),
          editAttributesAction,
          ...actions.slice(editTagsIndex + 1),
        ];
      }
    }

    return actions;
  }, [FILE_ACTIONS, canDelete, categoryId]);

  const handleActionClick = useCallback(
    (actionLabel: string, actionOnClick?: () => void) => {
      if (actionLabel === "Delete") {
        setShowDeleteConfirm(true);
        return;
      }

      if (actionLabel === "Edit tags") {
        setShowEditTags(true);
        return;
      }

      if (actionLabel === "Edit attributes") {
        setShowEditAttributes(true);
        return;
      }

      actionOnClick?.();
    },
    []
  );

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    deleteFileHandler();
  };

  const handleCreateComment = async (text: string, fileId: string) => {
    setIsCreating(true);
    try {
      await createComment({ text, fileId });
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
      toast.success("Comment added", "Your comment has been posted.");
    } catch (error) {
      toast.error(
        "Failed to add comment",
        error instanceof Error ? error.message : "An error occurred"
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateComment = async (id: string | number, text: string) => {
    setIsUpdating(true);
    try {
      await updateComment(id, { text });
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
      toast.success("Comment updated", "Your comment has been updated.");
    } catch (error) {
      toast.error(
        "Failed to update comment",
        error instanceof Error ? error.message : "An error occurred"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteComment = async (id: string | number) => {
    try {
      await deleteComment(id);
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
      toast.success("Comment deleted", "The comment has been removed.");
    } catch (error) {
      toast.error(
        "Failed to delete comment",
        error instanceof Error ? error.message : "An error occurred"
      );
    }
  };

  return (
    <>
      <div className="flex h-full w-96 flex-col border-l border-grid-200 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-grid-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-ink-900">Details</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close details"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <FilePreviewSection file={file} onImageClick={onImageClick} />

          <FileNavigationHeader
            fileName={file.name}
            canNavigatePrev={canNavigatePrev}
            canNavigateNext={canNavigateNext}
            onNavigate={onNavigate}
          />

          <FileMetadataSection
            file={file}
            attributeOrder={attributeOrder}
            categoryAttributes={categoryAttributes || []}
          />

          <FileActionsGrid
            actions={visibleActions}
            onActionClick={handleActionClick}
          />
          <CommentsSection
            comments={comments}
            onCreateComment={handleCreateComment}
            onUpdateComment={handleUpdateComment}
            onDeleteComment={handleDeleteComment}
            isCreating={isCreating}
            isUpdating={isUpdating}
            fileId={file.id}
          />
        </div>
      </div>
      <DeleteFileDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
      <EditTagsDialog
        open={showEditTags}
        onOpenChange={setShowEditTags}
        file={file}
      />
      {categoryId && (
        <EditFileAttributesDialog
          open={showEditAttributes}
          onOpenChange={setShowEditAttributes}
          file={file}
          categoryAttributes={categoryAttributesForEdit}
          isLoading={isLoadingAttributes}
        />
      )}
    </>
  );
}
